'use client';
import { faEnvelope, faExclamationCircle, faEyeSlash, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalClose, ModalContent, ModalDescription, ModalHeading, Tooltip } from '@pickleballinc/react-ui';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import classNames from 'classnames';
import clsx from 'clsx';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useSession } from '@/lib/SessionProvider';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { EventDetailsDrawer } from '@/tournament/components/events/EventDetailsDrawer';
import { proBracketLevelIDs } from '@/utils/helpers/constants';
import { getBracketsAppRedirectURL } from '@/utils/helpers/getBracketsAppRedirectURL';
import { getConnectDUPRURL } from '@/utils/helpers/getConnectDUPRURL';
import { isCurrentUser } from '@/utils/helpers/helpers';

export interface TourneyPlayer {
	imgUrl: string;
	fullName: string;
	gender: string;
	age: number;
	location: string;
	slug: string;
	playerDuprActive: boolean;
	playerId: string;
	events: PlayerEvent[];
}

export interface PlayerEvent {
	currentSequence: number;
	bracketFormatId: number;
	currentSequencePoolId: string;
	totalPools: number;
	showDraws: boolean;
	activityId: string;
	tourneyId: string;
	bracketLevelId: number;
	attendeeActivityID: string;
	title: string;
	date: string;
	status: EventStatus;
	isOnWaitlist: boolean;
	isOnLottery: boolean;
	needAPartner: boolean;
	hidePlayers: boolean;
	isPartnerRegistered: boolean;
	partnerId?: string;
	partnerName?: string;
	partnerUrl?: string;
	partnerDuprActive?: boolean;
}

export interface EventStatus {
	text: string;
	id: number;
}

interface PlayerRowProps {
	data: TourneyPlayer;
	needAPartnerOnly?: boolean;
	tourneySlug: string;
	isManager: boolean;
}

export const getEventStatusClass = (statusId: number) => {
	return classNames(
		'w-max rounded-md border px-2 py-1 text-sm text-gray-500 border-gray-500 bg-white font-medium',
		[2, 5, 6].includes(statusId) && '!text-blue-600 !bg-white !border-blue-600',
		statusId === 3 && '!text-success-600 !bg-white !border-success-600',
		statusId === 4 && '!text-warning-600 !bg-warning-50 !border-warning-600'
	);
};

export const getNeedAPartnerURL = (aaId: string) => {
	const url = new URL(`https://contact.${process.env.NEXT_PUBLIC_FRONT_URL}/contacts/tournament/partner/${aaId.toLowerCase()}`);
	return url.toString();
};

export const PlayerRow = ({ data, needAPartnerOnly = false, tourneySlug, isManager }: PlayerRowProps) => {
	const { imgUrl, fullName, gender, age, location, events, slug, playerDuprActive, playerId } = data;
	const [showEventDetails, setShowEventDetails] = useState(false);
	const [bracketModalOpen, setBracketModalOpen] = useState(false);

	const { session } = useSession();

	const [tourneyId, setTourneyId] = useState('');
	const [activityId, setActivityId] = useState('');

	return (
		<>
			{(!needAPartnerOnly || (needAPartnerOnly && events.some((e) => e.needAPartner))) && (
				<div className="rounded-lg border bg-white">
					<div className="flex flex-row gap-2 border-b p-2 sm:p-3">
						<img className="size-16 rounded-full border" src={imgUrl} alt="" />
						<div className="flex flex-col justify-evenly">
							<div>
								<CustomLink
									href={`/tournaments/${tourneySlug}/player-results/${slug}`}
									className="text-sm font-bold text-blue-600 hover:underline sm:text-base"
								>
									{fullName}
								</CustomLink>
								{!playerDuprActive && (
									<>
										{isCurrentUser(session, playerId) ? (
											<span>
												<a
													className="text-sm text-blue-600 underline"
													href={getConnectDUPRURL(playerId, 't', window.location.href, window.location.href)}
												>
													Connect To DUPR
												</a>
											</span>
										) : (
											<span className="text-sm text-gray-500">(Not Connected to DUPR)</span>
										)}
									</>
								)}
							</div>
							<div className="inline-flex flex-wrap items-center gap-1 text-xs font-medium text-gray-900 sm:gap-2 sm:text-sm">
								<div>Gender: {gender}</div>
								<div className="block h-4 w-px bg-gray-600" />
								<div>
									{age > 0 ? (
										<>Age: {age}</>
									) : (
										<Tooltip placement="bottom">
											<Tooltip.Trigger asChild>
												<FontAwesomeIcon icon={faEyeSlash} className="ml-0.5" />
											</Tooltip.Trigger>
											<Tooltip.Content className="w-52 rounded bg-black/75 p-2 text-center text-sm text-white">
												Player age hidden by player
											</Tooltip.Content>
										</Tooltip>
									)}
								</div>
								<div className="block h-4 w-px bg-gray-600" />
								<div>{location}</div>
							</div>
						</div>
					</div>
					<div>
						<div className="overflow-auto text-left">
							<table className="w-full overflow-y-scroll rounded-b-lg">
								<thead className="text-left text-gray-600 ">
									<tr>
										<th className="px-4 py-2 text-sm font-medium">Events Registered</th>
										<th className="px-4 py-2 text-sm font-medium">Partner</th>
										<th className="px-4 py-2 text-sm font-medium">Event</th>
									</tr>
								</thead>
								<tbody>
									{events &&
										events.map((event) => {
											if (!needAPartnerOnly || (needAPartnerOnly && event.needAPartner))
												return (
													<PlayerEventRow
														key={event.activityId}
														setBracketModalOpen={setBracketModalOpen}
														event={event}
														showEventDetails={false}
														setShowEventDetails={setShowEventDetails}
														setTourneyId={setTourneyId}
														setActivityId={setActivityId}
														isManager={isManager}
														tourneySlug={tourneySlug}
													/>
												);

											return;
										})}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			<EventDetailsDrawer
				tourneyId={tourneyId}
				activityId={activityId}
				setShowEventDetails={setShowEventDetails}
				showEventDetails={showEventDetails}
			/>

			<Modal onOpenChange={() => setBracketModalOpen(!bracketModalOpen)} open={bracketModalOpen} backdropClose={false}>
				<ModalContent className="flex w-[400px] flex-col justify-center">
					<ModalHeading className="flex w-full items-center justify-between">
						<div className="flex-1 text-xl font-semibold capitalize leading-7 text-gray-900">Draws Status</div>
						<ModalClose className="ml-auto">
							<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500" />
						</ModalClose>
					</ModalHeading>
					<ModalDescription className="mt-4 text-base font-normal text-gray-600">Draws not available at this time.</ModalDescription>
				</ModalContent>
			</Modal>
		</>
	);
};

interface PlayerEventRowProps {
	showEventDetails: boolean;
	setShowEventDetails: Dispatch<SetStateAction<boolean>>;
	setTourneyId: Dispatch<SetStateAction<string>>;
	setActivityId: Dispatch<SetStateAction<string>>;
	event: PlayerEvent;
	isManager: boolean;
	setBracketModalOpen: Dispatch<SetStateAction<boolean>>;
	tourneySlug: string;
}

const PlayerEventRow = ({ event, setShowEventDetails, setTourneyId, setActivityId, setBracketModalOpen, isManager }: PlayerEventRowProps) => {
	const { session } = useSession();

	const { hasPermission } = useAuth();

	const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

	const [eventBracketRedirectURL, setEventBracketRedirectURL] = useState<string>('');

	useEffect(() => {
		if (proBracketLevelIDs.includes(event.bracketLevelId) || (session?.user && session?.user?.uuid)) {
			const url = getBracketsAppRedirectURL(
				event.tourneyId,
				event.activityId,
				event.status.id,
				event.currentSequence,
				event.bracketFormatId,
				event.currentSequencePoolId,
				event.totalPools
			);

			setEventBracketRedirectURL(url.toString());
		} else {
			const url = new URL(`${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/`);
			url.searchParams.set('flow', 'SelfService');
			url.searchParams.set('continue', window.location.href);

			setEventBracketRedirectURL(url.toString());
		}
	}, []);

	return (
		<>
			<tr className="odd:bg-gray-50 even:bg-white">
				<td className="w-[400px] min-w-[400px]">
					<div className="flex flex-col gap-1 px-4 py-2">
						<div className="line-clamp-2 min-w-48 text-sm font-semibold text-gray-900 ">{event.title}</div>
						<div className="flex flex-row items-center gap-2 text-xs sm:text-sm">
							{event.date}{' '}
							{event.status.id !== 1 && (
								<div className={clsx(getEventStatusClass(event.status.id), 'text-xs')}>
									<span>&bull; </span>
									{event.status.text}
								</div>
							)}
							{event.isOnWaitlist && <div className={clsx(getEventStatusClass(4), 'text-xs')}>Waiting List</div>}
							{event.isOnLottery && <div className={clsx(getEventStatusClass(0), 'text-xs')}>Lottery</div>}
						</div>
					</div>
				</td>
				<td className="w-[300px] min-w-[300px] px-4 py-2 text-sm">
					{event.needAPartner ? (
						<a
							className="block text-blue-500 hover:underline"
							target="_blank"
							href={getNeedAPartnerURL(event.attendeeActivityID)}
							aria-label="Link to send partner request (opens in a new tab)"
						>
							<FontAwesomeIcon icon={faEnvelope} /> Send partner request
						</a>
					) : (
						<>
							{event.partnerUrl ? (
								<>
									{!event.isPartnerRegistered && (
										<Tooltip placement="bottom">
											<Tooltip.Trigger asChild>
												<FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-error-600" />
											</Tooltip.Trigger>
											<Tooltip.Content className="rounded bg-black/75 p-2 text-sm text-white">Not registered</Tooltip.Content>
										</Tooltip>
									)}
									<a className="text-blue-500 hover:underline" href={event.partnerUrl}>
										{event.partnerName}
									</a>
								</>
							) : (
								<div>{event.partnerName}</div>
							)}

							{event.partnerId && !event.partnerDuprActive && (
								<>
									{isCurrentUser(session, event.partnerId) ? (
										<div>
											<a
												className="text-sm text-blue-600 underline"
												href={getConnectDUPRURL(event.partnerId, 't', window.location.href, window.location.href)}
											>
												Connect To DUPR
											</a>
										</div>
									) : (
										<div className="text-sm text-gray-500">(Not Connected to DUPR)</div>
									)}
								</>
							)}
						</>
					)}
				</td>
				<td className="w-[120px] px-4 py-2">
					<div className="flex flex-row gap-2 text-gray-600">
						<Button
							size="sm"
							onClick={() => {
								setShowEventDetails(true);
								setActivityId(event.activityId);
								setTourneyId(event.tourneyId);
							}}
							variant="link"
							className="font-normal text-blue-500 underline hover:!text-blue-600"
						>
							Details
						</Button>

						{!event.showDraws && [2, 3, 4, 6].includes(event.status.id) ? (
							<>
								<div className=" block w-px bg-gray-400"></div>
								<button onClick={() => setBracketModalOpen(true)} className="text-sm text-blue-500 underline hover:!text-blue-600">
									Draw
								</button>
							</>
						) : event.status.id !== 1 &&
						  (proBracketLevelIDs.includes(event.bracketLevelId) || isSuperAdmin || isManager || event.showDraws) ? (
							<>
								<div className="block w-px bg-gray-400"></div>
								<a href={eventBracketRedirectURL} className="text-sm text-blue-500 underline hover:!text-blue-600">
									Draw
								</a>
							</>
						) : null}
					</div>
				</td>
			</tr>
		</>
	);
};
